import { keepPreviousData, useQuery } from '@tanstack/solid-query';
import { whatsappGraphql } from './client';
import { whatsappKeys } from './keys';

export interface WhatsappChat {
  chatJid: string;
  lastBody: string;
  lastFromMe: boolean;
  lastTsUnixMs: string;
  count: string;
  /** Best-effort display name (counterparty push name); empty if unknown. */
  name: string;
}

const CHATS_QUERY = `query WhatsappChats($accountId: String!) {
  user {
    whatsappChats(accountId: $accountId) {
      chatJid
      lastBody
      lastFromMe
      lastTsUnixMs
      count
      name
    }
  }
}`;

export async function fetchWhatsappChats(
  accountId: string
): Promise<WhatsappChat[]> {
  const data = await whatsappGraphql<{
    user: { whatsappChats: WhatsappChat[] };
  }>(CHATS_QUERY, { accountId });
  return data.user.whatsappChats;
}

/** Conversations for one account, polled so new inbound chats appear live. */
export function useWhatsappChatsQuery(
  accountId: () => string | undefined,
  refetchInterval = 15000
) {
  return useQuery(() => {
    const id = accountId();
    return {
      queryKey: whatsappKeys.chats(id ?? ''),
      queryFn: () => fetchWhatsappChats(id as string),
      enabled: !!id,
      // Keep the previous account's chats visible while the new account's
      // query loads, so switching accounts doesn't blank the whole panel
      // (a fallback-less <Suspense> upstream would otherwise flash empty).
      placeholderData: keepPreviousData,
      // A flapping gateway makes this throw transiently. Retry a couple times
      // to ride out a blip, and on a settled error back off to a slow poll
      // (not stop) so the list self-recovers once the gateway returns. With
      // keepPreviousData the last good chats stay visible meanwhile (no flash).
      retry: 2,
      refetchInterval: (query) =>
        query.state.status === 'error' ? 30000 : refetchInterval,
    };
  });
}
